import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPatients, getPatientConsultations } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Calendar, ClipboardList, Loader2, Scale, UserRound } from "lucide-react";
import { toast } from "sonner";

function coercePatientsArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.patients)) return payload.patients;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function coerceConsultationsArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  if (Array.isArray(payload.consultations)) return payload.consultations;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function patientFullName(patient) {
  return [patient?.nombres, patient?.apellidos].filter(Boolean).join(" ").trim() || "Paciente sin nombre";
}

function consultationId(consultation) {
  return consultation?.id || consultation?._id || "";
}

function consultationDate(consultation) {
  return consultation?.fecha_consulta || consultation?.created_at || null;
}

function formatPracticeDate(value) {
  if (!value) return "Fecha no disponible";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return date.toLocaleDateString("es-GT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function isPracticePatient(patient) {
  return !(patient?.caso_id ?? "").trim();
}

function countScales(consultation) {
  const escalas = consultation?.escalas;
  if (!escalas || typeof escalas !== "object") return 0;
  return Object.keys(escalas).length;
}

export default function AulaMisPracticas() {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPractices = useCallback(async () => {
    setLoading(true);
    try {
      const patientsRes = await getPatients();
      const academicPatients = coercePatientsArray(patientsRes?.data).filter(isPracticePatient);

      const nested = await Promise.all(
        academicPatients.map(async (patient) => {
          const patientId = patient?.id || patient?._id;
          if (!patientId) return [];

          try {
            const consultsRes = await getPatientConsultations(patientId);
            const consults = coerceConsultationsArray(consultsRes?.data);
            return consults.map((consultation) => ({ patient, consultation }));
          } catch {
            return [];
          }
        })
      );

      const flat = nested
        .flat()
        .filter(({ consultation }) => consultationId(consultation))
        .sort((a, b) => {
          const da = new Date(consultationDate(a.consultation) || 0).getTime();
          const db = new Date(consultationDate(b.consultation) || 0).getTime();
          return db - da;
        });

      setPractices(flat);
    } catch {
      toast.error("No se pudieron cargar tus prácticas");
      setPractices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPractices();
  }, [fetchPractices]);

  return (
    <div className="mx-auto max-w-5xl space-y-8 animate-fade-in">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Mis prácticas
        </h1>
        <p className="max-w-2xl text-base text-slate-600">
          Aquí aparecen las VGI que has completado
        </p>
      </header>

      {loading ? (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-violet-700">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <p className="text-sm font-medium">Cargando prácticas...</p>
        </div>
      ) : practices.length === 0 ? (
        <Card className="border-violet-200/80 bg-white">
          <CardContent className="flex min-h-[280px] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-600">
              <ClipboardList className="h-6 w-6" strokeWidth={1.75} />
            </div>
            <div className="space-y-1">
              <p className="text-base font-semibold text-slate-900">
                Aún no has completado ninguna práctica
              </p>
              <p className="text-sm text-slate-600">
                Crea un paciente ficticio y completa tu primera Valoración Geriátrica Integral.
              </p>
            </div>
            <Button
              asChild
              className="rounded-xl bg-violet-600 text-white hover:bg-violet-700"
            >
              <Link to="/aula/practicas/nueva">Iniciar primera práctica</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {practices.map(({ patient, consultation }) => {
            const id = consultationId(consultation);
            const name = patientFullName(patient);
            const scales = countScales(consultation);
            return (
              <Card
                key={id}
                className="border-violet-200/80 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-100 bg-violet-50 text-violet-700">
                        <UserRound className="h-5 w-5" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-base font-semibold text-slate-900">{name}</p>
                        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                          <Calendar className="h-3.5 w-3.5 shrink-0" />
                          {formatPracticeDate(consultationDate(consultation))}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      <p>
                        <span className="font-medium text-slate-800">Diagnóstico: </span>
                        {patient.diagnostico_principal || "Sin diagnóstico registrado"}
                      </p>
                      <p className="flex items-center gap-1.5">
                        <Scale className="h-3.5 w-3.5 text-violet-600" />
                        <span className="font-medium text-slate-800">{scales}</span>
                        {scales === 1 ? " escala aplicada" : " escalas aplicadas"}
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    variant="outline"
                    className="shrink-0 rounded-xl border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-900"
                  >
                    <Link to={`/aula/practicas/${id}`}>Ver práctica</Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
